import { useState, forwardRef } from 'react'
import { Input, type InputProps } from './Input'

// Matches every valid email the backend will accept.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function validateEmail(value: string): string | undefined {
  if (!value) return undefined
  if (!EMAIL_RE.test(value)) return 'Enter a valid email address'
  return undefined
}

// Extends Input's own props, not the raw HTML attributes: Input redefines
// `size` as a height token (sm / md) to match DatePicker and DropdownSelect,
// and inheriting the DOM's numeric `size` here would put the two at odds.
interface Props extends Omit<InputProps, 'type' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  label?: string
  error?: string // external error (e.g. server-side); overrides internal
  required?: boolean
}

// Email input that self-validates on blur.
// Stores the raw string value - no transformation applied.
// Pass `error` to show an external error (e.g. from server response);
// if omitted, an inline error appears after the first blur with a bad value.
export const EmailInput = forwardRef<HTMLInputElement, Props>(function EmailInput(
  { value, onChange, label, error: externalError, required, ...rest },
  ref,
) {
  const [touched, setTouched] = useState(false)

  const internalError = touched ? validateEmail(value) : undefined
  const displayError = externalError ?? internalError

  return (
    <Input
      ref={ref}
      type="email"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => setTouched(true)}
      error={displayError}
      required={required}
      {...rest}
    />
  )
})
